import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import type { EducationalMaterialData } from '../../../services/api';
import { useLocale } from "../../../locales";

interface EducationalMaterialModalProps {
  onClose: () => void;
  onSelect: (url: string) => void;
  currentUrl?: string;
  materials: EducationalMaterialData | null;
  type: 'video' | 'others';
}

type MaterialItem = {
  title: string;
  url: string;
};

const EducationalMaterialModal: React.FC<EducationalMaterialModalProps> = ({
  onClose,
  onSelect,
  currentUrl,
  materials,
  type,
}) => {
  const { t } = useLocale();
  const contentRef = useRef<HTMLDivElement>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const getItemName = useCallback((url: string) => {
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      const fileName = pathname.split('/').pop() || url;
      return fileName.length > 50 ? fileName.substring(0, 50) + '...' : fileName;
    } catch {
      return url.length > 50 ? url.substring(0, 50) + '...' : url;
    }
  }, []);

  useEffect(() => {
    contentRef.current?.focus();
  }, []);

  useEffect(() => {
    const listener = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', listener);
    return () => window.removeEventListener('keydown', listener);
  }, [onClose]);

  const items = useMemo<MaterialItem[]>(() => {
    if (!materials) return [];
    const rawItems = type === 'video' ? materials.video : materials.others;
    const safeItems = Array.isArray(rawItems) ? rawItems : [];
    return safeItems
      .map((item) => {
        if (typeof item === 'string') {
          return { title: getItemName(item), url: item };
        }
        if (item && typeof item === 'object') {
          const title = item.title?.trim() || getItemName(item.url);
          return { title, url: item.url };
        }
        return null;
      })
      .filter((item): item is MaterialItem => Boolean(item && item.url));
  }, [getItemName, materials, type]);

  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) {
      return items;
    }
    const query = searchQuery.toLowerCase();
    return items.filter(
      (item) =>
        item.title.toLowerCase().includes(query) ||
        item.url.toLowerCase().includes(query)
    );
  }, [items, searchQuery]);

  const handleSelect = useCallback(
    (url: string) => {
      onSelect(url);
      onClose();
    },
    [onSelect, onClose]
  );

  return (
    <div className="qe-modal" role="presentation">
      <div className="qe-modal__backdrop" onClick={onClose} />
      <div className="qe-modal__content qe-modal__content--media-library" ref={contentRef} tabIndex={-1}>
        <div className="qe-modal__header">
          <div className="qe-modal__header-content">
            <h2 className="qe-modal__title">
              {type === 'video' ? t("educationalMaterial.titleLectureVideos") : t("educationalMaterial.titleSolutionTexts")}
            </h2>
            <p className="qe-modal__subtitle">
              {type === 'video'
                ? t("educationalMaterial.subtitleLectureVideo")
                : t("educationalMaterial.subtitleSolutionText")}
            </p>
          </div>
          <button
            type="button"
            className="qe-button qe-button--ghost qe-button--icon"
            onClick={onClose}
            aria-label={t("educationalMaterial.close")}
          >
            ✕
          </button>
        </div>

        <div className="qe-modal__body">
          <div className="qe-media-library__search">
            <input
              type="text"
              className="qe-input"
              placeholder={
                type === 'video'
                  ? t("educationalMaterial.searchVideoPlaceholder")
                  : t("educationalMaterial.searchFilePlaceholder")
              }
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {!materials ? (
            <div className="qe-media-library__loading">
              <p>{t("educationalMaterial.loading")}</p>
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="qe-media-library__empty">
              <p>
                {searchQuery
                  ? t("educationalMaterial.noSearchResults")
                  : type === 'video'
                    ? t("educationalMaterial.noLectureVideos")
                    : t("educationalMaterial.noSolutionTexts")}
              </p>
            </div>
          ) : (
            <div className="qe-media-library__groups">
              <div className="qe-media-library__group">
                <h3 className="qe-media-library__group-title">
                  {type === 'video' ? t("educationalMaterial.videosTab") : t("educationalMaterial.filesTab")}
                </h3>
                <div className="qe-media-library__grid">
                  {filteredItems.map((item, index) => {
                    const isSelected = currentUrl === item.url;
                    return (
                      <div
                        key={`item-${index}`}
                        className={`qe-media-library__item ${isSelected ? 'qe-media-library__item--selected' : ''}`}
                        onClick={() => handleSelect(item.url)}
                      >
                        <div className="qe-media-library__item-audio">
                          {type === 'video' ? (
                            <svg
                              width="48"
                              height="48"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                              style={{ color: 'var(--qe-color-text-secondary)' }}
                            >
                              <path
                                d="M8 5v14l11-7z"
                                fill="currentColor"
                              />
                            </svg>
                          ) : (
                            <svg
                              width="48"
                              height="48"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                              style={{ color: 'var(--qe-color-text-secondary)' }}
                            >
                              <path
                                d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"
                                fill="currentColor"
                              />
                            </svg>
                          )}
                        </div>
                        <div className="qe-media-library__item-name">
                          {item.title}
                        </div>
                        <div className="qe-media-library__item-size">
                          {getItemName(item.url)}
                        </div>
                        {isSelected && (
                          <div className="qe-media-library__item-check">
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 20 20"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M16.667 5L7.5 14.167 3.333 10"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="qe-modal__footer">
          <button
            type="button"
            className="qe-button qe-button--secondary"
            onClick={onClose}
          >
            {t("educationalMaterial.cancel")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EducationalMaterialModal;
